import React, { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Grid, Paper, Typography, Button, TextField, InputAdornment,
  Select, MenuItem, FormControl, CircularProgress, Chip, IconButton, Tooltip,
  FormControlLabel, Hidden, TablePagination,
  AppBar, Toolbar, Checkbox
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import LinkIcon from '@material-ui/icons/Link';
import FilterListIcon from '@material-ui/icons/FilterList';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import {IsoRounded, ReportOffOutlined} from '@material-ui/icons'
import AssignmentIcon from '@material-ui/icons/Assignment';
import EventIcon from '@material-ui/icons/Event';
import { blue } from '@material-ui/core/colors';
import { DataGrid } from '@material-ui/data-grid';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { AppDataContext } from '../../../common/AppContext';
import { EvaluationCategoryData } from '../../../services';
import { useHistory } from 'react-router-dom';
import RouteConstants from '../../../common/RouteConstants';
import Loader from '../../../sharedComponents/Loader/Loader';
import Helper from '../../../common/Helper';
import EvaluationReport from './EvaluationReport';
import EvaluationTextReport from './EvaluationTextReport';
import ConfirmationDialog from "../../../sharedComponents/dialog/ConfirmationDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  appbarBackground: {
    backgroundColor: '#fff',
    color: 'red',
  },
  title: {
    flexGrow: 1,
  },
  addGroup: {
    marginLeft: '5px',
    marginRight: '5px',
    border: '1px solid #1d428a',
    borderRadius: '4px',
    padding: '6px 8px',
    fontSize: '0.875rem',
    color: '#fff',
    background: '#1d428a',
    '&:hover': {
      color: '#fff',
      background: '#1d428a',
    },
    '&:focus': {
      color: '#fff',
      background: '#1d428a',
    },
    '& svg': {
      marginRight: '5px',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  filtersRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  gridContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    '& .MuiDataGrid-columnHeaderWrapper': {
      backgroundColor: '#1d428a',
      color: '#ffffff',
      '& .MuiDataGrid-checkboxInput':{
         color: '#ffffff',
      }
    },
    // Hide the "select all" checkbox in the column header
    '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-checkboxInput': {
      display: 'none',
    },
  },
  mobileCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderLeft: '4px solid #1d428a',
    '&:hover': {
      boxShadow: theme.shadows[3],
    },
  },
  mobileCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  mobileCardMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  mobileMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
  },
  mobileActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(1),
    borderTop: '1px solid ' + theme.palette.divider,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8, 2),
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 64,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
}));

const CATEGORY_WEIGHTS = {
  'court presence': 30,
  'mechanics': 20,
  'play calling': 20,
  'communication': 15,
  'fitness': 15,
};

const getCategoryWeight = (name) =>
  CATEGORY_WEIGHTS[(name || '').trim().toLowerCase()] || 0;

const getScoreColor = (val) => {
  if (val >= 4) return { bg: '#dcfce7', color: '#16a34a' };
  if (val >= 3) return { bg: '#fef9c3', color: '#ca8a04' };
  if (val >= 2) return { bg: '#fee2e2', color: '#dc2626' };
  return { bg: '#f3f4f6', color: '#6b7280' };
};

const PAGE_SIZE = 20;

const EvaluationSearchPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { evaluationEvents, getUpdatedEvaluationEvents, addSnackMessages } = useContext(AppDataContext);

  const [evaluations, setEvaluations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterError, setFilterError] = useState('');

  // For the report dialogs
  const [reportEvaluation, setReportEvaluation] = useState(null);
  const [reportMode, setReportMode] = useState(null); // 'visual' or 'text'
  const [reportEvaluationsInfo, setReportEvaluationsInfo] = useState(null);
  const [evaluationCategories, setEvaluationCategories] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [tangibles, setTangibles] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedOfficialUID, setSelectedOfficialUID] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
const [reportForm, setReportForm] = useState(null);
  const [confirmProspectSwitch, setConfirmProspectSwitch] = useState({ open: false, uid: null, evaluationID: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, evaluation: null });

  useEffect(() => {
    getUpdatedEvaluationEvents();
    // Load categories, competencies, tangibles for report rendering
    EvaluationCategoryData.getAllEvaluationCategoryList().then(setEvaluationCategories);
    EvaluationCategoryData.getCompetenciesList().then((r) =>
      setCompetencies((r || []).filter((c) => c.competencyActive))
    );
    EvaluationCategoryData.getTangibleList().then((r) =>
      setTangibles((r || []).filter((t) => t.isActive))
    );
  }, []);

  const fetchEvaluations = useCallback(async (pageNum = 0) => {
    setIsLoading(true);
    try {
      const filter = {
        eventID: selectedEvent?.id || 0,
        scoutID: Helper.getOfficialId() || '00000000-0000-0000-0000-000000000000',
        searchTerm: searchTerm.trim(),
        pageNumber: pageNum + 1,
        pageSize: PAGE_SIZE,
      };
      const result = await EvaluationCategoryData.getEvaluationListPaged(filter);
      if (result) {
        setEvaluations(result.items || []);
        setTotalCount(result.totalCount || 0);
      } else {
        setEvaluations([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Failed to load evaluations:', err);
      setEvaluations([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [selectedEvent, searchTerm]);

  const handleSearch = () => {
    if (!selectedEvent && !searchTerm.trim()) {
      setFilterError('Please select an evaluation event or enter a prospect name to search.');
      return;
    }
    setFilterError('');
    setPage(0);
    fetchEvaluations(0);
    setSelectedRows([]);
    setSelectedOfficialUID(null);
    setReportEvaluation(null);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchEvaluations(newPage);
  };

  const handleRowClick = async (evaluation) => {
   
    if (evaluation.isDraft) {
      // Open form for editing
      history.push({
        pathname: RouteConstants.ADMIN_PROSPECT_EVALUATION_HOME,
        state: { evaluation, evaluationEvent: buildEventFromEval(evaluation) },
      });
    } else {
      // Ratings are already preloaded with the search results
      setReportEvaluation(evaluation);
      setShowReportDialog(true);
     
       const form = await buildFormFromEval(evaluation,true);
      setReportForm(form);
         setSelectedRows([])
         setSelectedOfficialUID(null);
         setReportEvaluationsInfo(null);
      setReportMode('visual');
    }
  };

  const handleShowReport = async () => {
    setIsLoading(true);
    setReportEvaluation(null);
    setReportMode('visual');
    try {
      const selectedEvaluations = (selectedRows || [])
        .map((id) => (typeof id === 'object'
          ? id
          : (displayedEvaluations || []).find((e) => e.evaluationID === id)))
        .filter(Boolean);
      setReportEvaluationsInfo(buildEvaluationsInfo(selectedEvaluations));
      const form = await buildFormFromEval();
      setReportForm(form);
      setShowReportDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Build a summary of the selected prospects & evaluations for the report header.
  const buildEvaluationsInfo = (selectedEvaluations) => {
    if (!selectedEvaluations || selectedEvaluations.length === 0) return null;

    const getProspectKey = (e) =>
      (e.officialUID != null && e.officialUID !== ''
        ? String(e.officialUID)
        : `${e.firstName || ''} ${e.lastName || ''}`.trim().toLowerCase());

    const prospectMap = new Map();
    selectedEvaluations.forEach((e) => {
      const key = getProspectKey(e);
      const name = `${e.firstName || ''} ${e.lastName || ''}`.trim();
      if (!prospectMap.has(key)) {
        prospectMap.set(key, { name: name || 'N/A', evaluations: [] });
      }
      const stateName = e.country === 1 ? (e.stateName || '') : (e.stateOther || '');
      const cityState = [e.city, stateName].filter(Boolean).join(' - ');
      prospectMap.get(key).evaluations.push({
        eventName: e.eventName || '',
        evaluationDate: e.evaluationDate ? Helper.FormatDate(e.evaluationDate) : '',
        eventFrom: e.eventFrom ? Helper.FormatDate(e.eventFrom) : '',
        eventTo: e.eventTo ? Helper.FormatDate(e.eventTo) : '',
        gameTypeName: e.gameTypeName || '',
        cityState,
        countryName: e.countryName || '',
        score: getWeightedScore(e),
      });
    });

    const prospects = Array.from(prospectMap.values());
    return {
      prospectCount: prospects.length,
      evaluationCount: selectedEvaluations.length,
      prospects,
    };
  };

  const buildEventFromEval = (evaluation, ) => ({
    id: evaluation.eventID,
    eventName: evaluation.eventName,
    gameType: evaluation.gameType,
    gameTypeName: evaluation.gameTypeName,
    country: evaluation.country,
    countryName: evaluation.countryName,
    state: evaluation.state,
    stateName: evaluation.stateName,
    stateOther: evaluation.stateOther,
    city: evaluation.city,
    eventFrom: evaluation.eventFrom,
    eventTo: evaluation.eventTo,
    scoutingCategoryName: evaluation.scoutingCategoryName,
    scoutingSubCategoryName: evaluation.scoutingSubCategoryName,
  });

  const getEvaluationSummaryText = (evaluation) =>
    (evaluation?.comments || evaluation?.evaluationSummary || evaluation?.summary || '').toString().trim();

  const consolidateEvaluationSummary = async (selectedEvaluations) => {
    const summaryTexts = selectedEvaluations
      .map((evaluation) => getEvaluationSummaryText(evaluation))
      .filter(Boolean);

    try {
      const result = await EvaluationCategoryData.getConsolidatedEvaluationSummary(summaryTexts);
      return typeof result === 'string' && result.trim() ? result.trim() : 'No summary found.';
    } catch (error) {
      console.error('Failed to consolidate evaluation summary:', error);
      return summaryTexts.length > 0 ? summaryTexts.join('\n\n') : 'No summary found.';
    }
  };

  const buildFormFromEval = async(evalu, isRowClick=false) => {
    const newRowIds = evalu ? [evalu.id] : [...selectedRows];
    const selectedEvaluations = (newRowIds || [])
      .map((id) => (typeof id === 'object' ? id : (displayedEvaluations || []).find((e) => e.evaluationID === id)))
      .filter(Boolean);

    if (selectedEvaluations.length === 0) {
      return {};
    }

    // Helper to safely get all ratings/comments for a key across all evaluations
    const getAllRatings = (keyFn) =>
      selectedEvaluations
        .map((evaluation) =>
          (evaluation.evaluationRatingList || [])
            .filter(keyFn)
            .map((r) => r.rating)
        )
        .flat();

    const getAllComments = (categoryID) =>
      selectedEvaluations
        .map((evaluation) =>
          (evaluation.commentsList || [])
            .filter((c) => c.categoryID === categoryID)
            .map((c) => c.comment)
        )
        .flat();

    // Use the first evaluation for static fields
    const firstEval = selectedEvaluations[0];
    const isConsolidated = selectedEvaluations.length > 1;

    const form = {
      id: firstEval.evaluationID,
      refereeName: `${firstEval.firstName || ''} ${firstEval.lastName || ''}`.trim(),
      evaluationDate: firstEval.evaluationDate
        ? Helper.FormatDate(firstEval.evaluationDate)
        : null,
      evaluationSummary: isConsolidated
        ? await consolidateEvaluationSummary(selectedEvaluations)
        : getEvaluationSummaryText(firstEval),
      status: firstEval.isDraft ? 'Draft' : 'Submitted',
    };

    // Get all unique subCategoryIDs and categoryIDs
    const allSubCategoryIDs = [
      ...new Set(
        selectedEvaluations
          .map((e) => e.evaluationRatingList || [])
          .flat()
          .map((r) => r.subCategoryID)
          .filter((id) => id && id > 0)
      ),
    ];
    const allCategoryIDs = [
      ...new Set(
        selectedEvaluations
          .map((e) => e.evaluationRatingList || [])
          .flat()
          .map((r) => r.categoryID)
          .filter((id) => id && id > 0)
      ),
    ];

    // Average subcategory ratings
    allSubCategoryIDs.forEach((subCategoryID) => {
      const ratings = getAllRatings((r) => r.subCategoryID === subCategoryID);
      if (ratings.length > 0) {
        const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        form[`sub_${subCategoryID}`] = Number(avg.toFixed(2));
      }
    });

    // Average category ratings (only where no subcategory)
    allCategoryIDs.forEach((categoryID) => {
      const ratings = getAllRatings(
        (r) => (!r.subCategoryID || r.subCategoryID === 0) && r.categoryID === categoryID
      );
      if (ratings.length > 0) {
        const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        form[`cat_${categoryID}`] = Number(avg.toFixed(2));
      }
    });

    // Comments: just take the first non-empty comment for each category on single reports.
    if (!isConsolidated) {
      allCategoryIDs.forEach((categoryID) => {
        const comments = getAllComments(categoryID).filter(Boolean);
        if (comments.length > 0) {
          form[`category_${categoryID}`] = comments[0];
        }
      });
    } else {
      // For multiple evaluations, consolidate each category through the backend AI service.
      const list = [];
      selectedEvaluations.forEach((evaluation) => {
        const commentList = (evaluation.commentsList || []).map((l) => ({
          notes: l.comment,
          categoryID: l.categoryID,
        }));
        list.push({ categories: commentList });
      });

      try {
        const allComments = await getConsolidatedComments(list);
        allComments.forEach((c) => {
          form[`category_${c.categoryID}`] = c.notes;
        });
      } catch (err) {
        console.log(err);
      }
    }

    return form;
  };

  const buildSelectedCompetencies = (evaluation) => {
    const map = {};
    (evaluation.evaluationRatingCompetencyList || []).forEach((rc) => {
      const key = rc.subCategoryID > 0
        ? `sub_comp_${rc.subCategoryID}`
        : `cat_comp_${rc.categoryID}`;
      if (!map[key]) map[key] = [];
      if (!map[key].includes(rc.competencyID)) {
        map[key].push(rc.competencyID);
      }
    });
    return map;
  };

  const handleSwitchReportMode = (mode) => {
    setReportMode(mode);
  };

const handleCloseReport = () => {
    setReportEvaluation(null);
    setReportMode(null);
    setShowReportDialog(false);
    setReportForm(null);
    setReportEvaluationsInfo(null);
};

  const currentUserID = Helper.getOfficialId();

  const handleEdit = (evaluation) => {
    history.push({
      pathname: RouteConstants.ADMIN_PROSPECT_EVALUATION_HOME,
      state: { evaluation, evaluationEvent: buildEventFromEval(evaluation) },
    });
  };

  const handleDelete = (evaluation) => {
    setConfirmDelete({ open: true, evaluation });
  };

  const handleDeleteResponse = async (confirmed) => {
    if (confirmed && confirmDelete.evaluation) {
      try {
        await EvaluationCategoryData.deleteEvaluation({ evaluationID: confirmDelete.evaluation.evaluationID });
        fetchEvaluations(page);
        addSnackMessages([{
          open: true,
          severity: 'success',
          message: 'Evaluation deleted successfully.',
        }]);
      } catch (err) {
        console.error('Failed to delete evaluation:', err);
        addSnackMessages([{
          open: true,
          severity: 'error',
          message: 'Failed to delete evaluation. Please try again.',
        }]);
      }
    }
    setConfirmDelete({ open: false, evaluation: null });
  };

  const handleProspectSwitchResponse = (confirmed) => {
    if (confirmed && confirmProspectSwitch.uid) {
      setSelectedOfficialUID(confirmProspectSwitch.uid);
      setSelectedRows([confirmProspectSwitch.evaluationID]);
    } else {
      // Force DataGrid to re-render with previous selection by creating a new array reference
      setSelectedRows((prev) => [...prev]);
    }
    setConfirmProspectSwitch({ open: false, uid: null, evaluationID: null });
  };

  // Filter evaluations client-side by status (API already paged/filtered by event)
  const displayedEvaluations = evaluations;

  const getProspectName = (e) =>
    `${e.firstName || ''} ${e.lastName || ''}`.trim() || 'Unnamed';

  const getWeightedScore = (e) => {
    const ratingList = e.evaluationRatingList || [];
    if (ratingList.length === 0) return '—';

    const activeCategories = (evaluationCategories || []).filter(cat => cat.categoryActive);
    if (activeCategories.length === 0) return '—';

    let totalWeightedScore = 0;
    let totalWeight = 0;

    activeCategories.forEach(cat => {
      const weight = getCategoryWeight(cat.categoryName);
      const activeSubs = (cat.subCategories || []).filter(s => s.subCategoryActive);

      let catScore = 0;
      if (activeSubs.length > 0) {
        const subRatings = activeSubs
          .map(sub => {
            const match = ratingList.find(r => r.subCategoryID === sub.id);
            return match ? match.rating : 0;
          })
          .filter(r => r > 0);
        if (subRatings.length > 0) {
          catScore = subRatings.reduce((a, b) => a + b, 0) / subRatings.length;
        } else {
          // Fallback for old-format evaluations rated at the category level
          const catMatch = ratingList.find(r => r.categoryID === cat.id && (!r.subCategoryID || r.subCategoryID === 0));
          catScore = catMatch ? catMatch.rating : 0;
        }
      } else {
        const match = ratingList.find(r => r.categoryID === cat.id && (!r.subCategoryID || r.subCategoryID === 0));
        catScore = match ? match.rating : 0;
      }

      totalWeightedScore += catScore * (weight / 100);
      totalWeight += weight;
    });

    return totalWeight > 0 ? totalWeightedScore.toFixed(2) : '—';
  };

  const getConsolidatedComments = async (payload) => {
    try {
      const comments = await EvaluationCategoryData.getConsolidatedComments(payload);
      return comments;
    } catch (error) {
      console.error('Failed to fetch consolidated comments:', error);
      return [];
    }
  };

  return (
    <>
      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Evaluation Search
          </Typography>
          <IconButton edge="end" aria-label="Map Prospects" onClick={() => history.push(RouteConstants.ADMIN_MAP_OFFICIALS)} className={classes.addGroup}>
            <LinkIcon />MAP PROSPECTS
          </IconButton>
          <IconButton edge="end" aria-label="New Evaluation" onClick={() => history.push(RouteConstants.ADMIN_PROSPECT_EVALUATION_HOME)} className={classes.addGroup}>
            <AddIcon />NEW EVALUATION
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box p={3}>

      {/* Filters */}
      <Box className={classes.filtersRow}>
        <Box style={{ flex: 1, minWidth: 280, maxWidth: 450 }}>         
          <Autocomplete
            value={selectedEvent}
            options={evaluationEvents || []}
            getOptionLabel={(option) =>
              option.eventName
                ? `${option.eventName}${option.eventFrom ? ` (${option.eventFrom}${option.eventTo ? ` — ${option.eventTo}` : ''})` : ''}`
                : ''
            }
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={(e, newValue) => setSelectedEvent(newValue)}
            size="small"
            fullWidth
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Select event to search..." />
            )}
          />
        </Box>

        <TextField
          variant="outlined"
          size="small"
          placeholder="Prospect name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          style={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Button edge="end" aria-label="Search" variant="contained" size='small' color='primary' onClick={handleSearch} disabled={isLoading} className={classes.addGroup}>
          {/* {isLoading ? <CircularProgress size={14} style={{ color: '#fff' }} /> : null} */}
          SEARCH
        </Button>

        <Button edge="end" variant="contained" color='primary' aria-label="Reset" size='small' onClick={() => {
            setSelectedEvent(null);
            setSearchTerm('');
            setFilterError('');
            setPage(0);
            setEvaluations([]);
            setSelectedRows([]);
            setSelectedOfficialUID(null);
            setTotalCount(0);
          }} className={classes.addGroup}>
            RESET
        </Button>
       {selectedRows && selectedRows.length > 0 && (
  <Button edge="end" aria-label="Show" size='small' variant="contained" color='primary' onClick={handleShowReport} className={classes.addGroup}>
    Show Report
  </Button>
)}
      </Box>

      {filterError && (
        <Typography variant="body2" style={{ color: '#d32f2f', padding: '8px 0' }}>
          {filterError}
        </Typography>
      )}

      {/* Results */}
      {displayedEvaluations.length === 0 && !isLoading ? (
        <Box className={classes.emptyState}>
          <AssignmentIcon className={classes.emptyIcon} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Evaluations Found
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ maxWidth: 400 }}>
            {selectedEvent
              ? 'No evaluations match the selected event and filters.'
              : 'Select an event and click Search to find evaluations.'}
          </Typography>
        </Box>
      ) : (
        <>
        {/* Desktop DataGrid */}
        <Hidden smDown>
        <Paper className={classes.gridContainer}>
          <div style={{ height: 'calc(100vh - 250px)', width: '100%' }}>
            <div style={{ display: 'flex', height: '100%' }}>
              <div style={{ flexGrow: 1 }}>
                <DataGrid
                  rows={displayedEvaluations.map((e) => ({ ...e, id: e.evaluationID }))}
                  columns={[
                    // {
                    //   field: 'isSelected',
                    //   headerName: 'Select All',
                    //   renderHeader: (params) => (
                    //     <Checkbox
                    //       checked={params.allSelected}
                    //       //onChange={(e) => handleSelectAll(e.target.checked)}
                    //     />
                    //   ),
                    //   flex: 0.4,
                    //   headerAlign: 'center',
                    //   align: 'center',
                    //   headerClassName: 'super-app-theme--header',
                    //   disableColumnMenu: true,
                    //    sortable: false,
                    //   renderCell: (params) => (
                    //     <Checkbox checked={params.isSelected} />
                    //   ),
                    // },
                    {
                      field: 'prospectName',
                      headerName: 'Prospect',
                      flex: 1,
                      headerClassName: 'super-app-theme--header',
                      disableColumnMenu: true,
                      valueGetter: (params) => `${params.row.firstName || ''} ${params.row.lastName || ''}`.trim() || 'Unnamed',
                    },
                    {
                      field: 'poolName',
                      headerName: 'Pool',
                      flex: 0.8,
                      headerClassName: 'super-app-theme--header',
                      disableColumnMenu: true,
                      valueGetter: (params) => params.row.poolName || '—',
                    },
                    {
                      field: 'eventName',
                      headerName: 'Event',
                      flex: 1,
                      headerClassName: 'super-app-theme--header',
                      disableColumnMenu: true,
                    },
                    {
                      field: 'evaluationDate',
                      headerName: 'Date',
                      flex: 0.7,
                      headerClassName: 'super-app-theme--header',
                      disableColumnMenu: true,
                      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : '—',
                    },
                    {
                      field: 'scoutName',
                      headerName: 'Scout',
                      flex: 1,
                      headerClassName: 'super-app-theme--header',
                      disableColumnMenu: true,
                    },
                    {
                      field: 'score',
                      headerName: 'Score',
                      flex: 0.6,
                      headerAlign: 'center',
                      align: 'center',
                      headerClassName: 'super-app-theme--header',
                      disableColumnMenu: true,
                      sortable: false,
                      renderCell: (params) => {
                        const score = getWeightedScore(params.row);
                        if (score === '—') return '—';
                        const num = parseFloat(score) || 0;
                        const style = getScoreColor(num);
                        return (
                          <Chip
                            label={`${score}/5`}
                            size="small"
                            style={{ backgroundColor: style.bg, color: style.color, fontWeight: 700, fontSize: '0.75rem', height: 26 }}
                          />
                        );
                      },
                    },
                    {
                      field: 'isDraft',
                      headerName: 'Status',
                      flex: 0.6,
                      headerAlign: 'center',
                      align: 'center',
                      headerClassName: 'super-app-theme--header',
                      disableColumnMenu: true,
                      renderCell: (params) => (
                        <Chip
                          label={params.value ? 'Draft' : 'Submitted'}
                          size="small"
                          color={params.value ? 'default' : 'primary'}
                          variant="outlined"
                          style={{ fontSize: '0.7rem', height: 24, fontWeight: 600 }}
                        />
                      ),
                    },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      flex: 0.8,
                      headerAlign: 'right',
                      align: 'right',
                      headerClassName: 'super-app-theme--header',
                      disableColumnMenu: true,
                      sortable: false,
                      renderCell: (params) => (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, width: '100%' }}>
                          <Tooltip title={params.row.isDraft ? 'Edit Draft' : 'View Report'}>
                            <IconButton size="small" onClick={() => handleRowClick(params.row)}>
                              {params.row.isDraft
                                ? <EditIcon style={{ color: blue[500] }} fontSize="small" />
                                : <VisibilityIcon style={{ color: blue[500] }} fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          {params.row.scoutID === currentUserID && (
                            <>
                              {!params.row.isDraft && (
                                <Tooltip title="Edit Evaluation">
                                  <IconButton size="small" onClick={() => handleEdit(params.row)}>
                                    <EditIcon style={{ color: '#1d428a' }} fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Delete Evaluation">
                                <IconButton size="small" onClick={() => handleDelete(params.row)}>
                                  <DeleteIcon style={{ color: '#dc2626' }} fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      ),
                    },
                  ]}
                  pageSize={PAGE_SIZE}
                  rowsPerPageOptions={[PAGE_SIZE]}
                  rowCount={totalCount}
                  paginationMode="server"
                  page={page}
                  onPageChange={(newPage) => handlePageChange(null, newPage)}
                  disableSelectionOnClick
                   checkboxSelection
                   selectionModel={selectedRows}
                   onSelectionModelChange={(newSelectionModel) => {
                    // Deselecting rows
                    if (newSelectionModel.length < selectedRows.length) {
                      // User unchecked a row — keep only remaining rows with same officialUID
                      const remaining = newSelectionModel.filter(id => {
                        const row = displayedEvaluations.find(e => e.evaluationID === id);
                        return row && row.officialUID === selectedOfficialUID;
                      });
                      if (remaining.length === 0) {
                        setSelectedOfficialUID(null);
                      }
                      setSelectedRows(remaining);
                      return;
                    }

                    // Find the newly checked id
                    const added = newSelectionModel.filter(id => !selectedRows.includes(id));
                    if (added.length === 0) return;

                    const clickedRow = displayedEvaluations.find(e => e.evaluationID === added[added.length - 1]);
                    if (!clickedRow) return;

                    const uid = clickedRow.officialUID;

                    // If a different officialUID is already selected, confirm switch
                    if (selectedOfficialUID && uid !== selectedOfficialUID) {
                      setConfirmProspectSwitch({ open: true, uid, evaluationID: clickedRow.evaluationID });
                      return;
                    }

                    // Same officialUID or no prior selection — toggle the clicked row
                    setSelectedOfficialUID(uid);
                    const updatedSelection = [...selectedRows];
                    const idx = updatedSelection.indexOf(clickedRow.evaluationID);
                    if (idx > -1) {
                      updatedSelection.splice(idx, 1);
                    } else {
                      updatedSelection.push(clickedRow.evaluationID);
                    }
                    // Ensure only rows with the same officialUID remain
                    const filtered = updatedSelection.filter(id => {
                      const row = displayedEvaluations.find(e => e.evaluationID === id);
                      return row && row.officialUID === uid;
                    });
                    if (filtered.length === 0) {
                      setSelectedOfficialUID(null);
                    }
                    setSelectedRows(filtered);
                   }}
                />
              </div>
            </div>
          </div>
        </Paper>
        </Hidden>

        {/* Mobile Cards */}
        <Hidden mdUp>
          {displayedEvaluations.map((evaluation) => {
            const score = getWeightedScore(evaluation);
            const scoreNum = parseFloat(score) || 0;
            const scoreStyle = getScoreColor(scoreNum);
            return (
              <Paper key={evaluation.evaluationID} variant="outlined" className={classes.mobileCard}>
                <Box className={classes.mobileCardHeader}>
                  <Box flex={1}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {getProspectName(evaluation)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gridGap={4}>
                    <Chip
                      label={evaluation.isDraft ? 'Draft' : 'Submitted'}
                      size="small"
                      color={evaluation.isDraft ? 'default' : 'primary'}
                      variant="outlined"
                      style={{ fontSize: '0.65rem', height: 22, fontWeight: 600 }}
                    />
                    {score !== '—' && (
                      <Chip
                        label={`${score}/5`}
                        size="small"
                        style={{ backgroundColor: scoreStyle.bg, color: scoreStyle.color, fontWeight: 700, fontSize: '0.7rem', height: 22 }}
                      />
                    )}
                  </Box>
                </Box>
                <Box className={classes.mobileCardMeta}>
                  {evaluation.poolName && (
                    <span className={classes.mobileMetaItem}>
                      <AssignmentIcon style={{ fontSize: 14 }} />
                      {evaluation.poolName}
                    </span>
                  )}
                  {evaluation.eventName && (
                    <span className={classes.mobileMetaItem}>
                      <EventIcon style={{ fontSize: 14 }} />
                      {evaluation.eventName}
                    </span>
                  )}
                  {evaluation.evaluationDate && (
                    <span className={classes.mobileMetaItem}>
                      {new Date(evaluation.evaluationDate).toLocaleDateString()}
                      {evaluation.scoutName ? ` • ${evaluation.scoutName}` : ''}
                    </span>
                  )}
                </Box>
                <Box className={classes.mobileActions}>
                  <Box display="flex" alignItems="center" gridGap={4}>
                    <IconButton size="small" onClick={() => handleRowClick(evaluation)}>
                      {evaluation.isDraft
                        ? <EditIcon style={{ color: blue[500] }} fontSize="small" />
                        : <VisibilityIcon style={{ color: blue[500] }} fontSize="small" />}
                    </IconButton>
                    {evaluation.scoutID === currentUserID && (
                      <>
                        {!evaluation.isDraft && (
                          <IconButton size="small" onClick={() => handleEdit(evaluation)}>
                            <EditIcon style={{ color: '#1d428a' }} fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => handleDelete(evaluation)}>
                          <DeleteIcon style={{ color: '#dc2626' }} fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {evaluation.isDraft ? 'Edit Draft' : 'View Report'}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </Hidden>
        </>
      )}

      {/* Report Dialogs */}
      {showReportDialog && reportForm && (
        <EvaluationReport
          open={true}
          onClose={handleCloseReport}
          form={reportForm}
          evaluationCategories={evaluationCategories}
          competencies={competencies}
          selectedCompetencies={selectedRows.length==0?buildSelectedCompetencies(reportEvaluation):[]}
          tangibles={tangibles}
          selectedTangibles={selectedRows.length==0?reportEvaluation.evaluationTangibles || []:[]}
          selectedEvent={selectedRows.length==0?buildEventFromEval(reportEvaluation):[]}
          onSwitchToText={selectedRows.length==0?() => handleSwitchReportMode('text'):undefined}
          showEventDetails={selectedRows.length==0?true:false}
          evaluationsInfo={selectedRows.length==0?null:reportEvaluationsInfo}
        />
      )}

      {reportEvaluation && reportMode === 'text' && (
        <EvaluationTextReport
          open={true}
          onClose={() => handleSwitchReportMode('visual')}
          form={reportForm}
          evaluationCategories={evaluationCategories}
          competencies={competencies}
          selectedCompetencies={buildSelectedCompetencies(reportEvaluation)}
          tangibles={tangibles}
          selectedTangibles={reportEvaluation.evaluationTangibles || []}
          selectedEvent={buildEventFromEval(reportEvaluation)}
          onSwitchToVisual={() => handleSwitchReportMode('visual')}
        />
      )}
      </Box>

      <ConfirmationDialog
        open={confirmProspectSwitch.open}
        title="Switch Prospect"
        message="Do you want to select another prospect? Current selection will be cleared."
        handleResponse={handleProspectSwitchResponse}
        isDelete={false}
      />

      <ConfirmationDialog
        open={confirmDelete.open}
        title="Delete Evaluation"
        message={confirmDelete.evaluation ? `Are you sure you want to delete the evaluation for ${getProspectName(confirmDelete.evaluation)}?` : ''}
        handleResponse={handleDeleteResponse}
        isDelete={true}
      />
      <Loader isLoading={isLoading} />
    </>
  );
};

export default EvaluationSearchPage;
